import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import type { Theme as NivoTheme } from '@nivo/core';
import { felixTokens, radius } from '../../../theme';

/**
 * Tema base dos gráficos nivo derivado dos tokens do MUI.
 *
 * A paleta de SÉRIES fica de fora de propósito (`palette.charts` /
 * `palette.complementary` não trocam com light/dark). O que troca é a moldura:
 * eixo, tick, grid, legenda e tooltip — ver DESIGN.md → Gráficos:
 * texto `ink-60`, grid `rule`, tooltip `surface` + borda `rule`.
 *
 * Tick de eixo é número/data → IBM Plex Mono tabular, a assinatura do app.
 */
export default function useChartTheme(): NivoTheme {
  const theme = useTheme();

  return useMemo<NivoTheme>(
    () => ({
      background: 'transparent',
      text: {
        fontFamily: felixTokens.fontSans,
        fontSize: 12,
        fill: theme.palette.text.secondary,
      },
      axis: {
        domain: {
          line: {
            stroke: theme.palette.divider,
            strokeWidth: 1,
          },
        },
        ticks: {
          line: {
            stroke: theme.palette.divider,
            strokeWidth: 1,
          },
          // Tick é dado: mono tabular para não "dançar" entre valores.
          text: {
            fontFamily: felixTokens.fontMono,
            fontSize: 11,
            fontWeight: 400,
            fontVariantNumeric: 'tabular-nums',
            fill: theme.palette.text.secondary,
          },
        },
        legend: {
          text: {
            fontFamily: felixTokens.fontMono,
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '0.12em',
            fill: theme.palette.text.secondary,
          },
        },
      },
      grid: {
        line: {
          stroke: theme.palette.divider,
          strokeWidth: 1,
        },
      },
      crosshair: {
        line: {
          stroke: theme.palette.text.secondary,
          strokeWidth: 1,
          strokeOpacity: 0.6,
          strokeDasharray: '4 4',
        },
      },
      legends: {
        text: {
          fontFamily: felixTokens.fontMono,
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: '0.04em',
          fill: theme.palette.text.secondary,
        },
        ticks: {
          text: {
            fontFamily: felixTokens.fontMono,
            fontSize: 11,
            fontVariantNumeric: 'tabular-nums',
            fill: theme.palette.text.secondary,
          },
        },
      },
      labels: {
        text: {
          fontFamily: felixTokens.fontMono,
          fontSize: 11,
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
        },
      },
      dots: {
        text: {
          fontFamily: felixTokens.fontMono,
          fontSize: 11,
          fill: theme.palette.text.secondary,
        },
      },
      // O conteúdo do tooltip é sempre um Card do tema (surface + hairline +
      // shadow-overlay). O container do nivo só serve de casca: sem chrome
      // próprio, senão a borda e a sombra ficam duplicadas.
      tooltip: {
        container: {
          background: 'transparent',
          color: theme.palette.text.primary,
          fontFamily: felixTokens.fontSans,
          fontSize: 13,
          border: 'none',
          borderRadius: radius.lg,
          boxShadow: 'none',
          padding: 0,
        },
        basic: {
          fontFamily: felixTokens.fontSans,
          fontSize: 13,
          padding: '6px 10px',
        },
        chip: { borderRadius: radius.xs },
      },
    }),
    [theme],
  );
}
